import { Checkbox, CheckboxProps } from "@heroui/react";
import { cn, getLocalizedValue } from "@/utils";
import { t } from "@lingui/core/macro";
import dayjs from "dayjs";
import { GetCustomerSubscriptions } from "@/server-types";
import { AlertTriangle } from "lucide-react";

export type CustomerSubscription =
  GetCustomerSubscriptions["response"]["customerSubscriptionsWithUsage"][number];

export const CustomerSubscriptionItem = ({
  customerSubscription,
  isAvailable,
  ...checkboxProps
}: Omit<CheckboxProps, "children"> & {
  customerSubscription: CustomerSubscription;
  isAvailable: boolean;
}) => {
  const hasUnpaidInvoices = customerSubscription.invoices.some(
    (invoice) => invoice.amountPaid < invoice.amountDue,
  );

  return (
    <Checkbox
      classNames={{
        base: cn(
          "inline-flex max-w-md w-full bg-content1 m-0",
          "hover:bg-content2 items-center justify-start",
          "cursor-pointer rounded-lg gap-2 p-4 border-2 border-transparent",
          "data-[selected=true]:border-primary",
        ),
        label: "w-full",
      }}
      {...checkboxProps}
    >
      <div className="flex w-full flex-col gap-1">
        {!isAvailable && (
          <div className="text-sm font-medium text-red-500">
            {t`Naročnina ne vključuje izbranih storitev`}
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium">
              {customerSubscription.subscription
                ? getLocalizedValue(
                    customerSubscription.subscription.nameLocalized,
                  )
                : t`Naročnina po meri`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm">
            {customerSubscription.variation
              ? getLocalizedValue(customerSubscription.variation.nameLocalized)
              : t`Variacija po meri`}
          </span>
          <span className="text-xs text-gray-500">
            {t`Veljavno do`}{" "}
            {dayjs(customerSubscription.validTo).format("DD.MM.YYYY")}
          </span>
        </div>
        {hasUnpaidInvoices ? (
          <div className="flex items-center gap-2 text-sm font-medium text-red-500">
            <AlertTriangle size={16} />
            {t`Naročnina ima neplačane račune`}
          </div>
        ) : null}
      </div>
    </Checkbox>
  );
};
